/* eslint-disable react/jsx-filename-extension */
import { isString } from 'underscore';
import { assertIsDefined } from '@bingads-webui-campaign-react/debug-assert';
import { clicksColumn, conversionsColumn, impressionsColumn, WithTreeViewData } from '@bingads-webui-grid/well-known';
import { IColumn, TextField } from '@fluentui/react';
import React from 'react';
import { WithBulk } from './PanelSelector';
import { Model$ProductAttributeStatistic, Model$ListingCondition } from '../ApiType';
import { ListingGroupGridItemData } from '../interface';
import { ListingColumn } from '../rawColumns';

const conversionsFiledName = 'conversions';
const impressionsFiledName = 'impressions';
const clicksFiledName = 'clicks';
const productsFiledName = 'products';
const defaultValuePlaceholder = '-';
const firstColumnName = 'primary';
export interface ListingGroupTreeViewSelectorGridItem {
  key: string;
  // fileds
  [firstColumnName]: string;
  /**
   *  @default "-"
   */
  [conversionsFiledName]: number | string | undefined | null;
  /**
   *  @default "-"
   */
  [impressionsFiledName]: number | string | undefined | null;
  /**
   *  @default "-"
   */
  [clicksFiledName]: number | string | undefined | null;
  /**
   *  @default "-"
   */
  [productsFiledName]: number | string | undefined | null;

  data: {
    Attribute: string;
  }
}

const dataColumnWidth = 80;

export function buildTreeViewSelectorItems(selectedDimension: Model$ListingCondition, selectedDimensionGridData: Model$ProductAttributeStatistic[], childrenOfNode: WithTreeViewData<ListingGroupGridItemData>[]): ListingGroupTreeViewSelectorGridItem[] {
  const selectedDimensionGridDataMap: { [attributeName: string]: Model$ProductAttributeStatistic } = selectedDimensionGridData.reduce((cur, item) => {
    assertIsDefined(item.AttributeName);
    // eslint-disable-next-line no-param-reassign
    cur[item.AttributeName] = item;
    return cur;
  }, {});
  const domainDataItems: ListingGroupTreeViewSelectorGridItem[] = selectedDimension.DomainData?.map((item) => {
    const curItemDataitem = selectedDimensionGridDataMap[item.Id];
    assertIsDefined(item.Value);
    return {
      key: item.Id.toString(),
      [firstColumnName]: item.Value,
      [clicksFiledName]: curItemDataitem?.PerformanceMetrics.Clicks,
      [conversionsFiledName]: curItemDataitem?.PerformanceMetrics.Conversions,
      [impressionsFiledName]: curItemDataitem?.PerformanceMetrics.Impressions,
      [productsFiledName]: curItemDataitem?.ProductCount,
      data: {
        Attribute: item.Value,
      },
    };
  }) ?? [];

  const apiDataItems: ListingGroupTreeViewSelectorGridItem[] = selectedDimensionGridData.map((item) => {
    assertIsDefined(item.AttributeName);
    return {
      key: item.Id.toString(),
      [firstColumnName]: item.AttributeName,
      [clicksFiledName]: item?.PerformanceMetrics.Clicks,
      [conversionsFiledName]: item?.PerformanceMetrics.Conversions,
      [impressionsFiledName]: item?.PerformanceMetrics.Impressions,
      [productsFiledName]: item?.OfferCount,
      data: {
        Attribute: item.AttributeName,
      },
    };
  }) ?? [];

  const existingItems = buildTreeViewSelectorItemsByChildrenOfNode(selectedDimension, childrenOfNode);
  const tempDataItems = mergeTreeViewSelectorItems(domainDataItems, existingItems);
  return fillDataFromAPI(tempDataItems, apiDataItems, selectedDimension.HasDomainData);
}

export function buildTreeViewSelectorItemsWithoutStatistics(selectedDimension: Model$ListingCondition): ListingGroupTreeViewSelectorGridItem[] {
  const domainDataItems: ListingGroupTreeViewSelectorGridItem[] = selectedDimension.DomainData?.map((item) => {
    assertIsDefined(item.Value);
    return {
      key: item.Id.toString(),
      [firstColumnName]: item.Value,
      [clicksFiledName]: undefined,
      [conversionsFiledName]: undefined,
      [impressionsFiledName]: undefined,
      [productsFiledName]: undefined,
      data: {
        Attribute: item.Value,
      },
    };
  }) ?? [];

  const existingItems = buildTreeViewSelectorItemsByChildrenOfNode(selectedDimension, []);
  const tempDataItems = mergeTreeViewSelectorItems(domainDataItems, existingItems);
  return tempDataItems;
}

function fillDataFromAPI(candidateItems: ListingGroupTreeViewSelectorGridItem[], apiDataItems: ListingGroupTreeViewSelectorGridItem[], hasDomainData: boolean) {
  return overrideMergeByValue<ListingGroupTreeViewSelectorGridItem>(
    candidateItems,
    apiDataItems,
    x => x.data.Attribute,
    (b, index, arr) => {
      const newItem = { ...arr[index] };
      newItem[clicksFiledName] = b?.[clicksFiledName];
      newItem[conversionsFiledName] = b?.[conversionsFiledName];
      newItem[impressionsFiledName] = b?.[impressionsFiledName];
      newItem[productsFiledName] = b?.[productsFiledName];
      // eslint-disable-next-line no-param-reassign
      arr[index] = newItem;
    },
    !hasDomainData
  );
}

/**
 * Given two array, "base" and "additional".
 *
 * Shadow copy "base" array to "result" array.
 * If found any element in "additional" is existed in "base", call the callback.
 * Else add the element to the "result" array.
 *
 * @param a base array
 * @param b additional array
 * @param comparator a callback to compare whether two element is same
 * @param callback called when two element is the same according to `comparator`
 * @returns result array
 */
function overrideMergeByComparator<T>(a: T[], b: T[], comparator: (x: T, y: T) => boolean, callback: (b: T, index: number, arr: T[]) => void, appendNewItems:boolean) {
  const res = [...a];
  b.forEach((x) => {
    const index = res.findIndex(y => comparator(x, y));
    if (index >= 0) {
      callback(x, index, res);
    } else if (appendNewItems) {
      res.push(x);
    }
  });
  return res;
}

function overrideMergeByValue<T>(a: T[], b: T[], valuePicker: (x: T) => unknown, callback: (b: T, index: number, arr: T[]) => void, appendNewItems:boolean) {
  const comperator = (x: T, y: T) => {
    const xValue = valuePicker(x);
    const yValue = valuePicker(y);

    if (isString(xValue) && isString(yValue)) {
      return xValue.toLowerCase() === yValue.toLowerCase();
    }

    return xValue === yValue;
  };

  return overrideMergeByComparator(a, b, comperator, callback, appendNewItems);
}

export function mergeTreeViewSelectorItems(oldItems: ListingGroupTreeViewSelectorGridItem[], additionalItems: ListingGroupTreeViewSelectorGridItem[]) {
  return overrideMergeByValue<ListingGroupTreeViewSelectorGridItem>(
    oldItems,
    additionalItems,
    x => x.data.Attribute,
    // eslint-disable-next-line no-return-assign, no-param-reassign
    (b, index, arr) => arr[index] = b,
    true
  );
}

export function buildTreeViewSelectorItemsByChildrenOfNode(selectedDimension: Model$ListingCondition, childrenOfNode: WithTreeViewData<ListingGroupGridItemData>[]): ListingGroupTreeViewSelectorGridItem[] {
  const res: ListingGroupTreeViewSelectorGridItem[] = [];
  childrenOfNode.forEach((child) => {
    // filter out everything else
    if (child.Condition?.Attribute
      && selectedDimension.Operand === child.Condition.Operand
    ) {
      res.push({
        key: child.Id.toString(),
        [firstColumnName]: child.Condition.Attribute,
        [clicksFiledName]: undefined,
        [conversionsFiledName]: undefined,
        [impressionsFiledName]: undefined,
        [productsFiledName]: undefined,
        data: {
          Attribute: child.Condition.Attribute,
        },
      });
    }
  });
  return res;
}

const defaultRender: IColumn['onRender'] = (item, index, column) => {
  let value = item && column?.fieldName ? item[column.fieldName] : '';

  if (value === null || value === undefined) {
    value = defaultValuePlaceholder;
  }

  if (typeof value === 'boolean') {
    return value.toString();
  }

  return (
    <span title={value}>
      {value}
    </span>
  );
};

export function fillDataToBulkAddedItem(item: WithBulk<ListingGroupTreeViewSelectorGridItem>) {
  const isAddedByBulkEdit = !!item.__bulk;
  if (isAddedByBulkEdit) {
    if (!item.data) {
      // eslint-disable-next-line no-param-reassign
      item.data = {
        Attribute: item[firstColumnName],
      };
    } else {
      // eslint-disable-next-line no-param-reassign
      item.data.Attribute = item[firstColumnName];
    }
  }
}

const primaryColumnRender: IColumn['onRender'] = (item, index, column) => {
  const value = item && column?.fieldName ? item[column.fieldName] : '';

  const isEditableItem = Number.isInteger(item.key) && item.key < 0;
  if (!isEditableItem) {
    return (
      <span title={value}>
        {value}
      </span>);
  }
  return (
    <TextField
      // tricky, React does not know it is updated
      defaultValue={value}
      onChange={(event, newValue) => {
        assertIsDefined(column?.fieldName);
        // eslint-disable-next-line no-param-reassign
        item[column.fieldName] = newValue;
      }}
    />
  );
};

export function buildTreeViewSelectorColumns(primaryColumnId: string, primaryColumnName: string, i18n: any, includeDataColumns: boolean): IColumn[] {
  const primaryColumn: IColumn = {
    key: primaryColumnId,
    name: i18n.getString(`ProductsGrid_Column_${primaryColumnName}`),
    minWidth: 180,
    fieldName: firstColumnName,
    isRowHeader: true,
    onRender: primaryColumnRender,
  };
  if (!includeDataColumns) {
    return [primaryColumn];
  }
  const dataColumns: IColumn[] = [
    { ...conversionsColumn, filedName: conversionsFiledName },
    { ...impressionsColumn, filedName: impressionsFiledName },
    { ...clicksColumn, filedName: clicksFiledName },
    { ...ListingColumn, filedName: productsFiledName },
  ].map<IColumn>(column => ({
    key: column.key,
    name: i18n.getString(column.name),
    minWidth: dataColumnWidth,
    maxWidth: dataColumnWidth,
    fieldName: column.filedName,
    onRender: defaultRender,
  }));
  return [primaryColumn, ...dataColumns];
}
